import React from 'react';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, addDays, isSameDay } from 'date-fns';

interface WorkoutCalendarProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  availableSlots: Array<{
    start: Date;
    end: Date;
    isPreferred?: boolean;
  }>;
  selectedSlot: { start: Date; end: Date; } | null;
  onSlotSelect: (slot: { start: Date; end: Date; }) => void;
  existingWorkout?: {
    start: Date;
    end: Date;
    title: string;
  };
}

export function WorkoutCalendar({
  selectedDate,
  onDateChange,
  availableSlots,
  selectedSlot,
  onSlotSelect,
  existingWorkout
}: WorkoutCalendarProps) {
  const nextWeek = Array.from({ length: 7 }, (_, i) => addDays(new Date(), i));

  const isSlotSelected = (slot: { start: Date; end: Date; }) => {
    return selectedSlot && 
      isSameDay(slot.start, selectedSlot.start) && 
      format(slot.start, 'HH:mm') === format(selectedSlot.start, 'HH:mm');
  };

  const isExistingWorkoutTime = (slot: { start: Date; end: Date; }) => {
    return existingWorkout && 
      isSameDay(slot.start, existingWorkout.start) && 
      format(slot.start, 'HH:mm') === format(existingWorkout.start, 'HH:mm');
  };

  return (
    <div className="space-y-4">
      {/* Date Selection */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => onDateChange(addDays(selectedDate, -1))}
          className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-primary" />
          <span className="font-medium">{format(selectedDate, 'MMMM d, yyyy')}</span>
        </div>
        <button
          onClick={() => onDateChange(addDays(selectedDate, 1))}
          className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Week View */}
      <div className="grid grid-cols-7 gap-2">
        {nextWeek.map((date) => (
          <button
            key={date.toISOString()}
            onClick={() => onDateChange(date)}
            className={`p-2 rounded-lg text-center transition-colors ${
              isSameDay(date, selectedDate)
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted/50'
            }`}
          >
            <div className="text-xs text-muted-foreground">
              {format(date, 'EEE')}
            </div>
            <div className="text-lg font-semibold">
              {format(date, 'd')}
            </div>
          </button>
        ))}
      </div>

      {/* Time Slots */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="w-4 h-4" />
          <span>Available Times</span>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto theme-scrollbar">
          {availableSlots.map((slot, index) => {
            const isSelected = isSlotSelected(slot);
            const isExisting = isExistingWorkoutTime(slot);

            return (
              <motion.button
                key={`${format(slot.start, 'HH:mm')}-${index}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => onSlotSelect(slot)}
                disabled={isExisting}
                className={`w-full p-3 rounded-lg flex items-center justify-between transition-all relative group ${
                  isSelected
                    ? 'bg-primary text-primary-foreground ring-2 ring-primary'
                    : isExisting
                    ? 'bg-muted/30 cursor-not-allowed'
                    : 'bg-muted hover:bg-muted/70'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{format(slot.start, 'h:mm a')}</span>
                  {slot.isPreferred && (
                    <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full">
                      Optimal Time
                    </span>
                  )}
                  {isExisting && (
                    <span className="text-xs bg-warning/20 text-warning px-1.5 py-0.5 rounded-full">
                      Current Time
                    </span>
                  )}
                </div>
                <span className="text-sm text-muted-foreground">
                  {format(slot.end, 'h:mm a')}
                </span>

                {/* Selection Indicator */}
                {isSelected && (
                  <motion.div
                    layoutId="selection-indicator"
                    className="absolute inset-0 bg-primary/10 rounded-lg -z-10"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
}